import { showApiError, showSuccess } from "@/lib/toast";
import { LinkedLicence } from "@/lib/types";
import { deleteFirmLicence, getLinkedLicences } from "@/sideEffects/api";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ColumnDef } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { AxDataTable } from "@/components/ax/dataTable/AxDataTable";
import { DeleteRowConfirmationDialog } from "@/components/ax/dataTable/DeleteRowConfirmationDialog";
import { AddNewLicenceDialog } from "./AddNewLicenceDialog";
import { UpdateRowButton } from "@/components/ax/dataTable/UpdateRowButton";
import { ChangeLicenceExpiryDialog } from "./ChangeLicenceExpiryDialog";
import { useState } from "react";

const QUERY_KEY = "getLinkedLicences";

interface Props {
  firmGuid: string;
  firmId: number;
}

export const FirmLicences = ({ firmGuid, firmId }: Props) => {
  const { t } = useTranslation();

  const queryClient = useQueryClient();
  const removeLinkedLicenceMutation = useMutation({
    mutationFn: (firmLicenceGuid: string) => {
      return deleteFirmLicence(firmGuid, firmLicenceGuid);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      showSuccess(t("common.deletedSuccess"));
    },
    onError: showApiError,
  });

  const [selectedLicence, setSelectedLicence] = useState<LinkedLicence>();

  const columns: ColumnDef<LinkedLicence>[] = [
    {
      accessorKey: "playerName",
      header: t("licence.playerName"),
      cell: ({ row }) => (
        <span className="text-xs truncate">{row.original.playerName ? row.original.playerName : "-"}</span>
      ),
    },
    {
      accessorKey: "registerType",
      header: t("licence.registerType"),
    },
    {
      accessorKey: "licenceType",
      header: t("licence.licenceType"),
    },
    {
      accessorKey: "licenceExpiry",
      header: t("licence.licenceExpiry"),
      cell: ({ row }) => <span className="text-xs truncate">{row.original.licenceExpiry}</span>,
    },
    {
      accessorKey: "intId",
      header: () => "",
      cell: ({ row }) => {
        return (
          <div className="flex items-center justify-end gap-2">
            <DeleteRowConfirmationDialog
              onConfirmDelete={() => {
                removeLinkedLicenceMutation.mutate(row.original.firmLicenceGuid);
              }}
              isLoading={
                removeLinkedLicenceMutation.isPending &&
                removeLinkedLicenceMutation.variables === row.original.firmLicenceGuid
              }
            />
            <UpdateRowButton
              onClickUpdate={() => {
                console.log("update");
                setSelectedLicence(row.original);
              }}
            />
          </div>
        );
      },
    },
  ];

  return (
    <div>
      <AxDataTable
        columns={columns}
        fetcher={getLinkedLicences}
        getRowId={(row) => row.firmLicenceGuid}
        uniqQueryKey={QUERY_KEY}
        dynamicRequestParams={{
          FirmGuid: firmGuid,
        }}
        extraHeader={<AddNewLicenceDialog firmId={firmId} firmGuid={firmGuid} />}
      />
      {selectedLicence && (
        <ChangeLicenceExpiryDialog
          firmLicenceGuid={selectedLicence.firmLicenceGuid}
          expiryDate={selectedLicence.licenceExpiry}
          onSuccess={() => {
            setSelectedLicence(undefined);
          }}
        />
      )}
    </div>
  );
};
